// apps/samson/packages/client/src/hooks/use-debounce-function.ts

import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook that returns a debounced version of the provided function.
 * The debounced function will delay execution until after the specified delay
 * has passed since the last time it was invoked.
 * 
 * @param fn - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns A debounced version of the function
 */
export function useDebounceFunction<T extends (...args: any[]) => any>(
    fn: T,
    delay: number
): T {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const debouncedFn = useCallback(
        ((...args: Parameters<T>) => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set new timeout
            timeoutRef.current = setTimeout(() => {
                fn(...args);
            }, delay);
        }) as T,
        [fn, delay]
    );

    return debouncedFn;
}